import { wrapLabel } from './chart-utils';

/**
 * Geteilte X-Achsen-Logik fuer alle kartesischen Charts (Bar/Line/Area/Composed).
 * Loest das Mobil-Problem, dass viele/lange Kategorie-Labels ineinanderlaufen.
 */

export interface CategoryTickProps {
  x?: number;
  y?: number;
  payload?: { value?: string | number };
  /** Auf schmalen Viewports: Label gedreht (–35°) statt umgebrochen. */
  angled?: boolean;
}

/**
 * X-Achsen-Tick fuer Kategorie-Labels. Mit `interval={0}` an der Achse bleiben
 * alle Beschriftungen sichtbar, statt von Recharts wegen Überlappung verschluckt
 * zu werden. Zwei Modi:
 *  - normal: lange Labels brechen auf bis zu zwei Zeilen um, zentriert.
 *  - angled (schmaler Viewport / viele Kategorien): Label um –35° gedreht,
 *    rechtsbuendig — so kollidieren auch viele Werte nicht.
 */
export function CategoryTick({ x = 0, y = 0, payload, angled = false }: CategoryTickProps) {
  const label = String(payload?.value ?? '');
  if (angled) {
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          transform="rotate(-35)"
          textAnchor="end"
          dy="0.7em"
          fontSize={11}
          fill="var(--color-brand-muted, #6b7280)"
        >
          {label}
        </text>
      </g>
    );
  }
  const lines = wrapLabel(label);
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        textAnchor="middle"
        fontSize={12}
        fill="var(--color-brand-muted, #6b7280)"
      >
        {lines.map((line, i) => (
          <tspan key={i} x={0} dy={i === 0 ? 12 : 13}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
}

/**
 * Entscheidet anhand der gemessenen Containerbreite, ob die X-Kategorie-Labels
 * gedreht werden muessen, und berechnet die noetige Achsenhoehe.
 *
 * Gedreht wird, sobald die laengste umbrochene Zeile nicht mehr in die Breite
 * einer Kategorie passt (~6.5px/Zeichen plus etwas Luft).
 */
export function categoryAxis(opts: {
  data: Record<string, unknown>[];
  xAxisKey: string;
  containerWidth: number;
  horizontal?: boolean;
  xUnit?: string;
}): { angled: boolean; height: number } {
  const { data, xAxisKey, containerWidth, horizontal = false, xUnit } = opts;
  if (horizontal) return { angled: false, height: 30 };

  const labels = data.map((row) => String(row[xAxisKey] ?? ''));
  const lineCount = labels.reduce((m, l) => Math.max(m, wrapLabel(l).length), 1);
  const maxWrappedLineChars = labels.reduce(
    (m, l) => Math.max(m, ...wrapLabel(l).map((s) => s.length), 0),
    0,
  );
  const maxLabelChars = labels.reduce((m, l) => Math.max(m, l.length), 0);
  const perCategory =
    containerWidth > 0 ? containerWidth / Math.max(labels.length, 1) : Infinity;
  const angled = containerWidth > 0 && perCategory < maxWrappedLineChars * 6.5 + 12;

  const height = angled
    ? Math.min(150, Math.ceil(maxLabelChars * 6.5 * 0.6) + 18 + (xUnit ? 22 : 0))
    : Math.max(30, 12 + (lineCount - 1) * 13 + 8 + (xUnit ? 22 : 0));

  return { angled, height };
}
